/* =============================================================================
   assets/giveaway-popup.js
   Rustic & Main — Popup Manager

   Reads window._rmPopups[] — an array of block configs output by
   sections/popups.liquid. Finds the first block whose schedule window is
   currently open, then arms the trigger engine for that block.
   Only one popup fires per page load.

   Per-block config keys:
     id, type, klaviyoKey, klaviyoList, giveawayMonth,
     startDate, endDate, trigger, delayMs, scrollPct, pagesMin,
     cooldownDays, ignoreCooldown, showSms, showShare, excludedPagesGlobal,
     themeVariant (optional — e.g. 'americana'; applied as data-variant on overlay+banner)
============================================================================= */

(function () {
  'use strict';

  /* ── Storage keys ── */
  var KEY_PREFIX      = 'rm_popup_seen_'; /* + block.id */
  var KEY_SUBSCRIBED  = 'rm_klaviyo_subscribed';
  var KEY_PAGES       = 'rm_popup_pages';

  /* ── Page-visit counter (session) ── */
  function incrementPages() {
    try {
      var n = parseInt(sessionStorage.getItem(KEY_PAGES) || '0', 10) + 1;
      sessionStorage.setItem(KEY_PAGES, n.toString());
      return n;
    } catch (e) { return 1; }
  }
  function getPagesVisited() {
    try { return parseInt(sessionStorage.getItem(KEY_PAGES) || '0', 10); } catch (e) { return 0; }
  }

  /* ── Already globally subscribed? ── */
  function isGloballySubscribed(cfg) {
    if (cfg && cfg.ignoreCooldown) return false;
    try { return !!localStorage.getItem(KEY_SUBSCRIBED); } catch (e) { return false; }
  }
  function markSubscribed(cfg) {
    if (cfg && cfg.ignoreCooldown) return;
    try { localStorage.setItem(KEY_SUBSCRIBED, '1'); } catch (e) {}
  }

  /* ── Per-popup cooldown ── */
  function isOnCooldown(cfg) {
    if (cfg.ignoreCooldown) return false;
    try {
      var ts = localStorage.getItem(KEY_PREFIX + cfg.id);
      if (!ts) return false;
      return (Date.now() - parseInt(ts, 10)) < cfg.cooldownDays * 86400000;
    } catch (e) { return false; }
  }
  function markSeen(cfg) {
    if (cfg.ignoreCooldown) return;
    try { localStorage.setItem(KEY_PREFIX + cfg.id, Date.now().toString()); } catch (e) {}
  }

  /* ── Date scheduling ── */
  function isInScheduleWindow(cfg) {
    var now = Date.now();
    if (cfg.startDate) {
      var start = new Date(cfg.startDate + 'T00:00:00').getTime();
      if (now < start) return false;
    }
    if (cfg.endDate) {
      var end = new Date(cfg.endDate + 'T23:59:59').getTime();
      if (now > end) return false;
    }
    return true;
  }

  /* ── Page exclusions ── */
  function isExcludedPage(cfg) {
    var path = window.location.pathname;
    /* Always-excluded patterns */
    var always = ['/cart', '/account', '/login', '/register', '/password', '/challenge'];
    for (var i = 0; i < always.length; i++) {
      if (path.indexOf(always[i]) === 0) return true;
    }
    /* Section-level global exclusions */
    if (cfg.excludedPagesGlobal) {
      var handles = cfg.excludedPagesGlobal.split(',');
      for (var j = 0; j < handles.length; j++) {
        var h = handles[j].trim().toLowerCase();
        if (h && path.indexOf(h) !== -1) return true;
      }
    }
    return false;
  }

  /* ── Find active block ── */
  function findActiveBlock() {
    var popups = window._rmPopups;
    if (!popups || !popups.length) return null;

    // Find the single highest-priority popup whose schedule window is open.
    // Blocks are ordered in the theme editor: timed/seasonal blocks FIRST,
    // evergreen last. We pick only the first in-window, non-excluded block
    // as the candidate — there is exactly one active giveaway at a time.
    //
    // If that candidate is on cooldown (dismissed), we return null.
    // Falling through to a lower-priority popup after the user dismissed
    // the current one would feel like a bait-and-switch and ignore intent.
    var candidate = null;
    for (var i = 0; i < popups.length; i++) {
      var cfg = popups[i];
      if (isExcludedPage(cfg)) continue;
      if (!isInScheduleWindow(cfg)) continue;
      candidate = cfg;
      break;
    }

    if (!candidate) return null;
    if (isOnCooldown(candidate)) return null;
    return candidate;
  }

  /* ── DOM helpers ── */
  function q(id) { return document.getElementById(id); }

  /* ── Open / close ── */
  function applyVariant(el, cfg) {
    if (!el) return;
    if (cfg.themeVariant) {
      el.setAttribute('data-variant', cfg.themeVariant);
    } else {
      el.removeAttribute('data-variant');
    }
  }

  function openPopup(cfg) {
    /* On mobile with banner mode enabled — show banner instead of full popup */
    if (cfg.mobileBanner && window.innerWidth < 641) {
      openBanner(cfg);
      return;
    }
    var overlay = q('rm-popup-' + cfg.id);
    if (!overlay) return;
    applyVariant(overlay, cfg);
    markSeen(cfg);
    animateCountBar(cfg);
    overlay.classList.add('is-open');
    overlay.setAttribute('aria-hidden', 'false');
    overlay.removeAttribute('inert');
    document.body.style.overflow = 'hidden';
    // Delay focus until after the CSS transition completes (320ms)
    setTimeout(function () {
      var nameEl = q('rm-popup-name-' + cfg.id);
      if (nameEl) {
        nameEl.focus();
        // Fallback: if focus didn't land (e.g. browser blocked), try the first CTA
        if (document.activeElement !== nameEl) {
          var cta = q('rm-popup-cta1-' + cfg.id);
          if (cta) cta.focus();
        }
      }
    }, 350);
  }

  /* ── Banner open / close / dismiss ── */
  function openBanner(cfg) {
    var banner = q('rm-banner-' + cfg.id);
    if (!banner) return;
    applyVariant(banner, cfg);
    markSeen(cfg);
    banner.classList.add('is-visible');
    banner.setAttribute('aria-hidden', 'false');
    banner.removeAttribute('inert');
  }

  function closeBanner(cfg) {
    var banner = q('rm-banner-' + cfg.id);
    if (!banner) return;
    banner.classList.remove('is-visible');
    banner.setAttribute('aria-hidden', 'true');
    banner.setAttribute('inert', '');
  }

  function openFullFromBanner(cfg) {
    closeBanner(cfg);
    var overlay = q('rm-popup-' + cfg.id);
    if (!overlay) return;
    applyVariant(overlay, cfg);
    animateCountBar(cfg);
    overlay.classList.add('is-open');
    overlay.setAttribute('aria-hidden', 'false');
    overlay.removeAttribute('inert');
    document.body.style.overflow = 'hidden';
    setTimeout(function () {
      var nameEl = q('rm-popup-name-' + cfg.id);
      if (nameEl) nameEl.focus();
    }, 350);
  }

  function closePopup(cfg) {
    var overlay = q('rm-popup-' + cfg.id);
    if (!overlay) return;
    overlay.classList.remove('is-open');
    overlay.setAttribute('aria-hidden', 'true');
    overlay.setAttribute('inert', '');
    document.body.style.overflow = '';
  }

  /* ── Step navigation ── */
  function goStep(cfg, n) {
    var panels = ['s1', 's2', 's3'];
    panels.forEach(function (s, i) {
      var el = q('rm-popup-' + s + '-' + cfg.id);
      if (!el) return;
      if (i + 1 === n) { el.hidden = false; el.removeAttribute('hidden'); }
      else { el.hidden = true; }
    });
    setTimeout(function () {
      var panel = q('rm-popup-s' + n + '-' + cfg.id);
      if (!panel) return;
      var first = panel.querySelector('input:not([type=checkbox]), button');
      if (first) first.focus();
    }, 50);
  }

  /* ── Deadline bar — days remaining or live countdown in final 48hrs ── */
  function animateCountBar(cfg) {
    var labelEl = q('rm-popup-count-label-' + cfg.id);
    var numEl   = q('rm-popup-count-' + cfg.id);
    var fillEl  = q('rm-popup-fill-'  + cfg.id);
    if (!fillEl) return;

    var now         = new Date();
    var daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    var daysLeft    = daysInMonth - now.getDate();

    if (daysLeft <= 1) {
      /* ── Countdown mode — show live HH:MM:SS timer ── */
      if (labelEl) {
        labelEl.textContent = 'Ending In';
        labelEl.classList.add('is-urgent');
      }
      if (numEl) numEl.classList.add('is-urgent');

      /* Bar pulses near-empty in urgency red */
      fillEl.style.width = '4%';
      fillEl.style.background = '#C0392B';
      fillEl.style.animation = 'rm-popup-bar-pulse 1.5s ease-in-out infinite';

      /* Calculate ms until end of last day of month (23:59:59) */
      function getMsUntilMonthEnd() {
        var n   = new Date();
        var end = new Date(n.getFullYear(), n.getMonth() + 1, 0, 23, 59, 59, 999);
        return Math.max(0, end - n);
      }

      function formatCountdown(ms) {
        var totalSec = Math.floor(ms / 1000);
        var h = Math.floor(totalSec / 3600);
        var m = Math.floor((totalSec % 3600) / 60);
        var s = totalSec % 60;
        return (
          String(h).padStart(2, '0') + ':' +
          String(m).padStart(2, '0') + ':' +
          String(s).padStart(2, '0')
        );
      }

      function tick() {
        var ms = getMsUntilMonthEnd();
        if (numEl) numEl.textContent = formatCountdown(Math.max(0, ms));
        if (ms <= 0) {
          clearInterval(timerInterval);
          /* Month has rolled over — reinitialise the bar for the new month */
          fillEl.style.animation = '';
          animateCountBar(cfg);
        }
      }

      tick(); /* Run immediately so there's no blank flash */
      var timerInterval = setInterval(tick, 1000);

    } else {
      /* ── Normal mode — days remaining ── */
      if (labelEl) labelEl.textContent = daysLeft === 0 ? 'Closes today' : 'Giveaway open';
      if (numEl)   numEl.textContent   = daysLeft === 0 ? '' : daysLeft + (daysLeft === 1 ? ' day left' : ' days left');
      var pct = Math.round((daysLeft / daysInMonth) * 100);
      setTimeout(function () { fillEl.style.width = pct + '%'; }, 200);
    }
  }

  /* ── Date helpers ── */
  function entryMonthNow() {
    var d = new Date();
    return d.toLocaleString('en-US', { month: 'long' }) + ' ' + d.getFullYear();
  }
  function entryDateNow() {
    return new Date().toISOString().slice(0, 10);
  }

  /* ── Validation ── */
  function validEmail(v) { return v && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v); }
  function setError(id, msg) { var el = q(id); if (el) el.textContent = msg; }
  function clearError(id)    { var el = q(id); if (el) el.textContent = ''; }

  /* ── Klaviyo custom event ── */
  function trackGiveawayEntry(cfg, opts, entryMonth, entryDate) {
    if (!cfg.klaviyoKey) return Promise.resolve();
    return fetch(
      'https://a.klaviyo.com/client/events/?company_id=' + cfg.klaviyoKey,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'revision': '2026-04-15' },
        body: JSON.stringify({
          data: {
            type: 'event',
            attributes: {
              metric: { data: { type: 'metric', attributes: { name: 'Giveaway Entry' } } },
              profile: { data: { type: 'profile', attributes: { email: opts.email } } },
              properties: {
                giveaway_month:        entryMonth,
                giveaway_entry_date:   entryDate,
                giveaway_prize_amount: cfg.prizeAmount || '',
                giveaway_source:       'Popup'
              },
              value: 1
            }
          }
        })
      }
    ).catch(function (e) { console.warn('[Popup] Event track failed:', e); });
  }

  /* ── Klaviyo subscribe ── */
  function subscribeToKlaviyo(cfg, opts) {
    if (!cfg.klaviyoKey || !cfg.klaviyoList) {
      console.warn('[Popup] Klaviyo key or list ID missing for block', cfg.id);
      return Promise.resolve();
    }
    var now        = new Date();
    var entryDate  = now.toISOString().slice(0, 10);
    var entryMonth = now.toLocaleString('en-US', { month: 'long' }) + ' ' + now.getFullYear();

    var profileAttrs = {
      email:      opts.email,
      first_name: opts.firstName || '',
      properties: {
        giveaway_entry:        true,
        giveaway_month:        entryMonth,
        giveaway_entry_date:   entryDate,
        giveaway_source:       'Popup',
        giveaway_prize_amount: cfg.prizeAmount || '',
        interest_wedding_band:   !!(opts.interests && opts.interests.indexOf('wedding_band')   !== -1),
        interest_engagement_ring:!!(opts.interests && opts.interests.indexOf('engagement_ring') !== -1),
        interest_custom_design:  !!(opts.interests && opts.interests.indexOf('custom_design')   !== -1)
      }
    };

    if (opts.phone && !opts.skipSms) {
      var digits = opts.phone.replace(/[^\d+]/g, '');
      if (digits && !digits.startsWith('+')) digits = '+1' + digits;
      if (digits.length >= 10) {
        profileAttrs.phone_number = digits;
        profileAttrs.properties['sms_giveaway_consent'] = !!opts.smsConsent;
      }
    }

    return fetch(
      'https://a.klaviyo.com/client/subscriptions/?company_id=' + cfg.klaviyoKey,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'revision': '2026-04-15' },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'Giveaway Popup',
              profile: { data: { type: 'profile', attributes: profileAttrs } }
            },
            relationships: { list: { data: { type: 'list', id: cfg.klaviyoList } } }
          }
        })
      }
    ).then(function (res) {
      if (!res.ok && res.status !== 409) {
        return res.json().catch(function () { return {}; }).then(function (b) {
          throw new Error(b?.errors?.[0]?.detail || 'Subscribe failed (' + res.status + ')');
        });
      }
    });
  }

  /* ── Share buttons ── */
  function initShareButtons(cfg) {
    var btns = document.querySelectorAll('#rm-popup-' + cfg.id + ' .rm-popup-share-btn');
    btns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var type = btn.dataset.share;
        var url  = encodeURIComponent(window.location.origin);
        var text = encodeURIComponent('I just entered a giveaway from Rustic & Main!');
        if (type === 'facebook') {
          window.open('https://www.facebook.com/sharer/sharer.php?u=' + url, '_blank', 'width=600,height=400');
        } else if (type === 'twitter') {
          window.open('https://twitter.com/intent/tweet?text=' + text + '&url=' + url, '_blank', 'width=600,height=400');
        } else if (type === 'copy') {
          var orig = btn.textContent;
          navigator.clipboard.writeText(window.location.origin).then(function () {
            btn.textContent = 'Copied!';
            setTimeout(function () { btn.textContent = orig; }, 2000);
          }).catch(function () {
            var ta = document.createElement('textarea');
            ta.value = window.location.origin;
            document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta);
            btn.textContent = 'Copied!';
            setTimeout(function () { btn.textContent = orig; }, 2000);
          });
        }
      });
    });
  }

  /* ── Wire up a single active popup ── */
  function wirePopup(cfg) {
    var id = cfg.id;
    var capturedEmail = '';
    var capturedName  = '';
    var capturedInterests = [];

    /* Close listeners — backdrop + close btn + cont btn + esc */
    document.querySelectorAll('[data-close="' + id + '"]').forEach(function (el) {
      el.addEventListener('click', function () { closePopup(cfg); });
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        var overlay = q('rm-popup-' + id);
        if (overlay && overlay.classList.contains('is-open')) closePopup(cfg);
      }
    });

    /* Banner button listeners */
    var bannerOpenBtn = document.querySelector('[data-banner-open="' + id + '"]');
    bannerOpenBtn && bannerOpenBtn.addEventListener('click', function () {
      openFullFromBanner(cfg);
    });
    var bannerCloseBtn = document.querySelector('[data-banner-close="' + id + '"]');
    bannerCloseBtn && bannerCloseBtn.addEventListener('click', function () {
      closeBanner(cfg);
    });

    /* Step 1 CTA */
    var cta1 = q('rm-popup-cta1-' + id);
    cta1 && cta1.addEventListener('click', function () {
      var emailEl = q('rm-popup-email-' + id);
      var nameEl  = q('rm-popup-name-'  + id);
      var email   = emailEl ? emailEl.value.trim() : '';
      clearError('rm-popup-err1-' + id);
      if (!validEmail(email)) {
        setError('rm-popup-err1-' + id, 'Please enter a valid email address.');
        if (emailEl) emailEl.focus();
        return;
      }
      capturedEmail = email;
      capturedName  = nameEl ? nameEl.value.trim() : '';

      /* Collect interest checkboxes */
      var interestBoxes = document.querySelectorAll('#rm-popup-interests-' + id + ' input[type="checkbox"]');
      var interests = [];
      interestBoxes.forEach(function (cb) { if (cb.checked) interests.push(cb.value); });
      capturedInterests = interests;

      if (cfg.showSms && q('rm-popup-s2-' + id)) {
        goStep(cfg, 2);
      } else {
        cta1.disabled = true;
        subscribeToKlaviyo(cfg, { email: capturedEmail, firstName: capturedName, interests: interests, skipSms: true })
          .then(function () { return trackGiveawayEntry(cfg, { email: capturedEmail }, entryMonthNow(), entryDateNow()); })
          .catch(function (e) { console.error('[Popup]', e); })
          .finally(function () {
            markSubscribed(cfg);
            cta1.disabled = false;
            /* ── Lead event: GA4, Meta, Pinterest, dataLayer ── */
            if (window.rmTrack) {
              window.rmTrack.lead('Giveaway Entry', { prize: cfg.prizeAmount || '' });
            }
            goStep(cfg, 3);
          });
      }
    });

    /* Step 1 skip */
    var skip1 = q('rm-popup-' + id)?.querySelector('[data-action="skip1"]');
    skip1 && skip1.addEventListener('click', function () { closePopup(cfg); });

    /* Step 2 CTA */
    var cta2 = q('rm-popup-cta2-' + id);
    cta2 && cta2.addEventListener('click', function () {
      var phoneEl   = q('rm-popup-phone-' + id);
      var consentEl = q('rm-popup-sms-'   + id);
      var phone     = phoneEl   ? phoneEl.value.trim() : '';
      var consent   = consentEl ? consentEl.checked    : false;
      clearError('rm-popup-err2-' + id);
      if (phone && !consent) {
        setError('rm-popup-err2-' + id, 'Please check the consent box to include your phone number.');
        return;
      }
      cta2.disabled = true;
      subscribeToKlaviyo(cfg, { email: capturedEmail, firstName: capturedName, interests: capturedInterests, phone: phone, smsConsent: consent })
        .then(function () { return trackGiveawayEntry(cfg, { email: capturedEmail }, entryMonthNow(), entryDateNow()); })
        .catch(function (e) { console.error('[Popup]', e); })
        .finally(function () {
          markSubscribed(cfg);
          cta2.disabled = false;
          /* ── Lead event: GA4, Meta, Pinterest, dataLayer ── */
          if (window.rmTrack) {
            window.rmTrack.lead('Giveaway Entry', { prize: cfg.prizeAmount || '' });
          }
          goStep(cfg, 3);
        });
    });

    /* Step 2 skip — submit email-only then advance */
    var skip2 = q('rm-popup-' + id)?.querySelector('[data-action="skip2"]');
    skip2 && skip2.addEventListener('click', function () {
      subscribeToKlaviyo(cfg, { email: capturedEmail, firstName: capturedName, interests: capturedInterests, skipSms: true })
        .then(function () { return trackGiveawayEntry(cfg, { email: capturedEmail }, entryMonthNow(), entryDateNow()); })
        .catch(function (e) { console.error('[Popup]', e); });
      markSubscribed(cfg);
      /* ── Lead event: GA4, Meta, Pinterest, dataLayer ── */
      if (window.rmTrack) {
        window.rmTrack.lead('Giveaway Entry', { prize: cfg.prizeAmount || '' });
      }
      goStep(cfg, 3);
    });

    /* Enter key submits current step */
    var nameEl = q('rm-popup-name-' + id);
    var emailEl = q('rm-popup-email-' + id);
    var phoneEl = q('rm-popup-phone-' + id);

    function onEnterStep1(e) {
      if (e.key === 'Enter') { e.preventDefault(); cta1 && cta1.click(); }
    }
    function onEnterStep2(e) {
      if (e.key === 'Enter') { e.preventDefault(); cta2 && cta2.click(); }
    }

    nameEl  && nameEl.addEventListener('keydown', onEnterStep1);
    emailEl && emailEl.addEventListener('keydown', onEnterStep1);
    phoneEl && phoneEl.addEventListener('keydown', onEnterStep2);

    /* Clear email error on input */
    emailEl && emailEl.addEventListener('input', function () {
      emailEl.classList.remove('is-error');
      clearError('rm-popup-err1-' + id);
    });

    initShareButtons(cfg);
  }

  /* ── Trigger engine ── */
  function armTriggers(cfg) {
    var fired = false;

    function fire() {
      if (fired) return;
      fired = true;
      openPopup(cfg);
    }

    /* Always enforce minimum delay */
    var delayPassed = false;
    setTimeout(function () {
      delayPassed = true;
      /* For delay-only trigger, fire immediately once delay passes */
      if (cfg.trigger === 'delay') fire();
      /* For scroll_or_pages: if pages threshold already met, fire now */
      if (cfg.trigger === 'scroll_or_pages' && getPagesVisited() >= cfg.pagesMin) fire();
      /* For pages_visited-only: same */
      if (cfg.trigger === 'pages_visited' && getPagesVisited() >= cfg.pagesMin) fire();
    }, cfg.delayMs);

    /* Scroll trigger */
    if (cfg.trigger === 'scroll_or_pages' || cfg.trigger === 'scroll') {
      var onScroll = function () {
        if (!delayPassed) return;
        var scrolled = window.scrollY / Math.max(1, document.body.scrollHeight - window.innerHeight) * 100;
        if (scrolled >= cfg.scrollPct) {
          window.removeEventListener('scroll', onScroll);
          fire();
        }
      };
      window.addEventListener('scroll', onScroll, { passive: true });
    }

    /* Exit intent */
    if (cfg.trigger === 'exit_intent') {
      var onLeave = function (e) {
        if (!delayPassed || e.clientY > 10) return;
        document.removeEventListener('mouseleave', onLeave);
        fire();
      };
      document.addEventListener('mouseleave', onLeave);
    }
  }

  /* ── Boot ── */
  incrementPages();

  var activeCfg = findActiveBlock();
  if (!activeCfg) return;

  if (isGloballySubscribed(activeCfg)) return;

  function init() {
    wirePopup(activeCfg);
    armTriggers(activeCfg);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
